import { useState, type FormEvent } from 'react';
import type { CalendarAvailability, NotificationStats, NotifySettings } from '../api/types.js';
import { useNotificationStats, useNotifyEverything, useRestoreKind, useSaveNotifySettings } from '../hooks/use-notification-stats.js';
import { useConnectGoogle, useDisconnectGoogle, useGoogle } from '../hooks/use-google.js';
import { useMail } from '../hooks/use-mail.js';
import { Button } from './button.js';
import { SectionAction, SettingsPanel, SettingsSection, SkeletonLine } from './settings-section.js';

// How this server notifies the operator (#760 step 3): today's count against the cap, the digest schedule on
// a ruler of the day, and per type what was sent and opened, with the types downgraded for being ignored.

const KIND: Record<string, string> = {
  deployment: 'Deployments to approve',
  analysis: 'Analysed deployments',
  review: 'Reviews ready',
  replies: 'Draft replies',
  held: 'Held events',
  blocked: 'Held by the daily budget',
  waiting_for_input: 'Sessions waiting for you',
  digest: 'Digests',
  google: 'Google connection',
  ticket: 'Ticket plans',
};

/** HH:MM in Paris */
function hm(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(iso));
}

/** The calendar's effect on notifications, in one sentence */
export function calendarSentence(cal: NonNullable<NotificationStats['calendar']>): string {
  const who = `Following ${cal.email}'s calendar`;
  const n = cal.now;
  const until = n.until ? ` until ${hm(n.until)}` : '';
  if (n.state === 'away') return `${who}: away${until}. Digests wait for your return; urgent decisions still come.`;
  if (n.state === 'busy' || n.state === 'focus') {
    const what = n.state === 'busy' ? `in a meeting${until}` : `in focus time${until}`;
    return cal.deferred ? `${who}: ${what}, so the ${cal.deferred} digest waits for your next free slot.` : `${who}: ${what}. Digests wait for a free slot, urgent decisions come in short form.`;
  }
  if (n.state === 'free') return `${who}: free now. Digests wait while you are in a meeting.`;
  return `${who}. Digests wait while you are in a meeting.`;
}

/** A band on the day ruler while the calendar blocks the operator */
const BAND: Partial<Record<CalendarAvailability['state'], string>> = {
  busy: 'var(--color-warn-dim)', focus: 'var(--color-brand-dim)', away: 'var(--color-idle-dim)',
};

const RULER_START = 6 * 60;
const RULER_END = 22 * 60;

function minutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

/** Now in Paris: minutes since midnight and whether it is a weekday */
function parisNow(now = new Date()): { min: number; weekday: boolean } {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit', weekday: 'short', hourCycle: 'h23' })
    .formatToParts(now).map((p) => [p.type, p.value]));
  return { min: Number(parts.hour) * 60 + Number(parts.minute), weekday: !['Sat', 'Sun'].includes(parts.weekday) };
}

/**
 * Today's digests in Paris: whether today has any (none on a weekend when they go out on weekdays only), and the
 * next one still to come today, if any. The day ruler and the next-digest sentence both read it.
 */
export function digestsToday(settings: NotifySettings, now = new Date()): { today: boolean; next: string | null } {
  const times = [...settings.digestTimes].sort();
  const { min, weekday } = parisNow(now);
  const today = times.length > 0 && (weekday || !settings.weekdaysOnly);
  return { today, next: today ? (times.find((t) => minutes(t) > min) ?? null) : null };
}

/** The next digest, in words: « at 13:00 », « tomorrow at 09:00 », « on Monday at 09:00 » */
export function nextDigest(settings: NotifySettings, now = new Date()): string | null {
  const times = [...settings.digestTimes].sort();
  if (!times.length) return null;
  const { next } = digestsToday(settings, now);
  if (next) return `at ${next}`;
  if (!settings.weekdaysOnly) return `tomorrow at ${times[0]}`;
  const day = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Paris', weekday: 'short' }).format(now);
  return ['Fri', 'Sat'].includes(day) ? `on Monday at ${times[0]}` : `tomorrow at ${times[0]}`;
}

/** Today at HH:MM in Paris, as an ISO time */
export function parisToday(h: number, m: number, now = new Date()): string {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .formatToParts(now).map((p) => [p.type, p.value]));
  const wall = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute));
  const offset = Math.round((wall - now.getTime()) / 60_000) * 60_000;
  return new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), h, m) - offset).toISOString();
}

/**
 * « Notify everything », on demand for a few hours: a teammate needs reviews out of hours. Every decision goes out at
 * once instead of waiting for the digest, weekends included and outside the daily limit; what already waited goes
 * out when it is switched on.
 */
function NotifyEverything({ serverId, isAdmin, liveUntil }: { serverId: string; isAdmin: boolean; liveUntil: string | null }) {
  const live = useNotifyEverything(serverId);
  const [sent, setSent] = useState<number | null>(null);
  const { min } = parisNow();
  const switchOn = (until: string) => live.mutate(until, { onSuccess: (r) => setSent(r.flushed) });
  const choice = 'rounded-md px-2.5 py-1 text-sm focus-visible:outline focus-visible:outline-2 disabled:opacity-60 max-sm:min-h-10';
  const choiceStyle = { border: '1px solid var(--color-border-2)', color: 'var(--color-text)', backgroundColor: 'var(--color-surface)' };
  const error = live.error as Error | null;

  if (liveUntil) {
    return (
      <div className="mt-4 rounded-md px-3 py-2.5" style={{ borderLeft: '3px solid var(--color-brand)', backgroundColor: 'var(--color-brand-dim)' }}>
        <p className="text-sm" style={{ color: 'var(--color-text)' }}>
          Every decision is notified at once until <span className="font-semibold tabular-nums">{hm(liveUntil)}</span>, weekends included and outside the daily limit.
        </p>
        <p role="status" className="mt-0.5 text-xs" style={{ color: 'var(--color-text-2)' }}>
          {sent === null ? '' : sent ? `${sent} waiting ${sent === 1 ? 'decision' : 'decisions'} sent now.` : 'Nothing was waiting.'}
        </p>
        {isAdmin && (
          <button type="button" onClick={() => { setSent(null); live.mutate(null); }} disabled={live.isPending}
            className="mt-1 py-0.5 text-xs hover:underline focus-visible:outline focus-visible:outline-2" style={{ color: 'var(--color-brand-light)' }}>
            {live.isPending ? 'Stopping…' : 'Stop now'}
          </button>
        )}
        {error && <p role="alert" className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>Not changed: {error.message}</p>}
      </div>
    );
  }
  if (!isAdmin) return null;
  return (
    <div className="mt-4">
      <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>Someone needs your answers now? Get each decision as it comes, not in the digest:</p>
      <div className="mt-1.5 flex flex-wrap gap-2" role="group" aria-label="Notify everything">
        <button type="button" className={choice} style={choiceStyle} disabled={live.isPending} onClick={() => switchOn(new Date(Date.now() + 3600_000).toISOString())}>For 1 hour</button>
        <button type="button" className={choice} style={choiceStyle} disabled={live.isPending} onClick={() => switchOn(new Date(Date.now() + 4 * 3600_000).toISOString())}>For 4 hours</button>
        {min < 21 * 60 && (
          <button type="button" className={choice} style={choiceStyle} disabled={live.isPending} onClick={() => switchOn(parisToday(22, 0))}>Until 22:00</button>
        )}
      </div>
      {error && <p role="alert" className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>Not switched on: {error.message}</p>}
    </div>
  );
}

export function DayRuler({ settings, blocked, liveUntil, now = new Date() }: { settings: NotifySettings; blocked?: CalendarAvailability; liveUntil?: string | null; now?: Date }) {
  const { min } = parisNow(now);
  const pos = (m: number) => `${(Math.min(Math.max(m, RULER_START), RULER_END) - RULER_START) / (RULER_END - RULER_START) * 100}%`;
  const sorted = [...settings.digestTimes].sort();
  // The next digest if it is today; none after the day's last one, and none on a day without digests
  const { today, next } = digestsToday(settings, now);
  return (
    <>
    {!today && <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>No digest today (weekdays only)</p>}
    <div className="relative mt-3 h-14" aria-hidden="true" data-testid="day-ruler">
      <div className="absolute inset-x-0 top-7 h-px" style={{ backgroundColor: 'var(--color-border-2)' }} />
      {blocked && BAND[blocked.state] && blocked.until && (
        <span className="absolute top-[1.15rem] h-5 rounded-sm" style={{
          // until a later day (an absence): to the end of the ruler
          left: pos(min), width: `calc(${pos(minutes(hm(blocked.until)) > min ? minutes(hm(blocked.until)) : RULER_END)} - ${pos(min)})`,
          backgroundColor: BAND[blocked.state],
        }} />
      )}
      {liveUntil && (
        <span className="absolute top-[1.15rem] h-5 rounded-sm" style={{
          left: pos(min), width: `calc(${pos(minutes(hm(liveUntil)) > min ? minutes(hm(liveUntil)) : RULER_END)} - ${pos(min)})`,
          backgroundColor: 'var(--color-brand-dim)', border: '1px solid var(--color-brand)',
        }} />
      )}
      {[6, 9, 12, 15, 18, 21].map((h) => (
        <span key={h} className="absolute top-9 -translate-x-1/2 text-[10px] tabular-nums" style={{ left: pos(h * 60), color: 'var(--color-text-3)' }}>{h}h</span>
      ))}
      {sorted.map((t) => {
        const isNext = t === next;
        return (
          <span key={t} data-next={isNext || undefined} className="absolute top-0 flex -translate-x-1/2 flex-col items-center" style={{ left: pos(minutes(t)), opacity: today ? undefined : 0.4 }}>
            <span className="text-[11px] font-medium tabular-nums" style={{ color: isNext ? 'var(--color-brand-light)' : 'var(--color-text-2)' }}>{t}</span>
            <span className="mt-1 rounded-full" style={{
              width: isNext ? 12 : 9, height: isNext ? 12 : 9, marginTop: isNext ? 2 : 4,
              backgroundColor: isNext ? 'var(--color-brand)' : 'var(--color-surface)',
              border: `2px solid ${isNext ? 'var(--color-brand)' : 'var(--color-text-3)'}`,
              boxShadow: isNext ? '0 0 0 4px var(--color-brand-dim)' : undefined,
            }} />
          </span>
        );
      })}
      {min >= RULER_START && min <= RULER_END && (
        <span className="absolute top-5 h-5 w-px" style={{ left: pos(min), backgroundColor: 'var(--color-text-2)' }} title="now" />
      )}
    </div>
    </>
  );
}

function ScheduleForm({ settings, onDone, serverId }: { settings: NotifySettings; onDone: () => void; serverId: string }) {
  const save = useSaveNotifySettings(serverId);
  const [times, setTimes] = useState<string[]>(settings.digestTimes);
  const [weekdaysOnly, setWeekdaysOnly] = useState(settings.weekdaysOnly);
  const [cap, setCap] = useState(String(settings.dailyCap));
  const capNumber = Number(cap);
  const valid = times.length > 0 && times.every((t) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t)) && Number.isInteger(capNumber) && capNumber >= 1 && capNumber <= 100;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid) return;
    save.mutate({ digestTimes: times, weekdaysOnly, dailyCap: capNumber }, { onSuccess: onDone });
  }

  return (
    <form onSubmit={submit} className="mt-4 space-y-4 rounded-md p-4" style={{ backgroundColor: 'var(--color-surface-2)' }}>
      <fieldset>
        <legend className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>Digest times (Paris)</legend>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {times.map((t, i) => (
            <span key={i} className="flex items-center gap-1">
              <input type="time" value={t} aria-label={`Digest time ${i + 1}`} required
                onChange={(e) => setTimes(times.map((x, j) => (j === i ? e.target.value : x)))}
                className="rounded px-2 py-1 text-sm tabular-nums"
                style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border-2)', color: 'var(--color-text)' }} />
              {times.length > 1 && (
                <button type="button" onClick={() => setTimes(times.filter((_, j) => j !== i))} aria-label={`Remove ${t}`}
                  className="px-1 text-sm" style={{ color: 'var(--color-text-3)' }}>×</button>
              )}
            </span>
          ))}
          {times.length < 6 && (
            <button type="button" onClick={() => setTimes([...times, '12:00'])} className="text-sm hover:underline" style={{ color: 'var(--color-brand-light)' }}>
              Add a time
            </button>
          )}
        </div>
      </fieldset>
      <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text)' }}>
        <input type="checkbox" checked={weekdaysOnly} onChange={(e) => setWeekdaysOnly(e.target.checked)} />
        Weekdays only
      </label>
      <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text)' }}>
        At most
        <input type="number" min={1} max={100} value={cap} onChange={(e) => setCap(e.target.value)} aria-label="Notifications per day"
          className="w-16 rounded px-2 py-1 text-sm tabular-nums"
          style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border-2)', color: 'var(--color-text)' }} />
        notifications a day, urgent ones included
      </label>
      {save.isError && <p role="alert" className="text-sm" style={{ color: 'var(--color-error)' }}>Schedule not saved: {(save.error as Error).message}</p>}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!valid || save.isPending}>{save.isPending ? 'Saving…' : 'Save schedule'}</Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>Cancel</Button>
      </div>
    </form>
  );
}

function summary(s: NotificationStats): string {
  const next = nextDigest(s.settings);
  const sent = `${s.today} of ${s.settings.dailyCap} notifications today (daily limit).`;
  if (!s.queued) return `${sent} Nothing waits for the next digest${next ? ` (${next})` : ''}.`;
  return `${sent} ${s.queued === 1 ? 'One decision waits' : `${s.queued} decisions wait`} for the next digest${next ? `, ${next}` : ''}.`;
}

const CALENDAR_WRITE = 'https://www.googleapis.com/auth/calendar.events';
const CALENDAR_READ = 'https://www.googleapis.com/auth/calendar.events.readonly';
const GMAIL_READ = 'https://www.googleapis.com/auth/gmail.readonly';
const DOCS_READ = 'https://www.googleapis.com/auth/documents.readonly';
// Search and read the Drive, write Docs, Sheets and Slides (ADR-038): one reconnection grants them all
const DRIVE = ['https://www.googleapis.com/auth/drive.readonly', 'https://www.googleapis.com/auth/documents', 'https://www.googleapis.com/auth/spreadsheets', 'https://www.googleapis.com/auth/presentations'];

/** Below this many unopened notifications left before a downgrade, the type says so */
const WARN_UNTIL_DOWNGRADE = 5;

/** What a type risks: « Lowered one level if the next 2 go unopened. », or nothing while far from it */
export function downgradeWarning(k: NotificationStats['kinds'][number]): string | null {
  const n = k.untilDowngrade;
  if (k.downgraded || n == null || n > WARN_UNTIL_DOWNGRADE || !k.sent) return null;
  return n === 1 ? 'Lowered one level if the next one goes unopened.' : `Lowered one level if the next ${n} go unopened.`;
}

/**
 * The calendar the digests follow, in one line under the state. Its space is kept while the Google state loads, so
 * nothing below shifts when it arrives. Disconnecting is not here: it sits at the foot of the section, confirmed.
 */
function CalendarLine({ serverId, isAdmin, stats }: { serverId: string; isAdmin: boolean; stats: NotificationStats }) {
  const { data: google, error: googleError, isLoading } = useGoogle(serverId);
  const { data: mail } = useMail(serverId);
  const connect = useConnectGoogle(serverId);
  if (isLoading) return <SkeletonLine width="85%" />;
  if (!google) {
    return googleError
      ? <p className="mt-2 text-sm" style={{ color: 'var(--color-text-3)' }}>Calendar state not loaded: {(googleError as Error).message}</p>
      : null;
  }
  const cal = stats.calendar;
  const action = (label: string) => isAdmin && (
    <button type="button" onClick={() => connect.mutate()} disabled={connect.isPending}
      className="flex-shrink-0 py-0.5 text-xs hover:underline focus-visible:outline focus-visible:outline-2 max-sm:min-h-10" style={{ color: 'var(--color-brand-light)' }}>
      {connect.isPending ? 'Opening Google…' : label}
    </button>
  );
  const scopes = google.connection?.scopes ?? [];
  let text: string;
  let button: ReturnType<typeof action> = false;
  if (!google.configured) {
    text = 'Calendar not available yet: an admin sets up the Google connection once (docs/google-workspace.md).';
  } else if (google.connection?.status === 'revoked') {
    text = `Google refused the connection of ${google.connection.email}: digests no longer follow your calendar.`;
    button = action('Reconnect');
  } else if (google.connection && !scopes.some((sc) => sc === CALENDAR_WRITE || sc === CALENDAR_READ)) {
    // Connected without the calendar: the box was unticked on Google's consent screen, or the Workspace blocks it
    text = `Connected as ${google.connection.email}, but Google gave no access to the calendar. Reconnect and tick the calendar permission; if Google does not offer it, the Workspace admin blocks it.`;
    button = action('Reconnect Google');
  } else if (google.connection && cal) {
    text = calendarSentence(cal);
  } else if (google.connection) {
    text = `Connected as ${google.connection.email}. The calendar state comes with the next refresh.`;
  } else {
    text = 'Connect your Google account in this client\'s Workspace: digests then wait while you are in a meeting.';
    button = action('Connect Google');
  }
  // Connected before sessions could change the calendar (ADR-024): its own line, with its own action
  const readOnly = google.connection?.status === 'ok' && scopes.includes(CALENDAR_READ) && !scopes.includes(CALENDAR_WRITE);
  // Connected before mail was read (ADR-036), on a server that reads it: the same action, the Gmail permissions added
  const noMail = google.connection?.status === 'ok' && mail?.enabled === true && !scopes.includes(GMAIL_READ);
  // Connected before meeting notes were read (ADR-037): the same action, the Google Docs permission added
  const noDocs = google.connection?.status === 'ok' && !button && !readOnly && !noMail && !scopes.includes(DOCS_READ);
  // One reconnection grants both: the Drive's line only once the Docs one is gone
  const noDrive = google.connection?.status === 'ok' && !button && !readOnly && !noMail && !noDocs && DRIVE.some((s) => !scopes.includes(s));
  const row = 'mt-1 flex flex-col items-start gap-1';
  return (
    <>
      <div className={row}>
        <p className="max-w-[68ch] text-sm" style={{ color: google.connection?.status === 'revoked' ? 'var(--color-warn)' : 'var(--color-text-2)' }}>{text}</p>
        {button}
      </div>
      {readOnly && (
        <div className={row}>
          <p className="max-w-[68ch] text-sm" style={{ color: 'var(--color-text-2)' }}>Sessions can read this calendar but not change it: reconnect Google to allow changes you approve.</p>
          {action('Reconnect Google')}
        </div>
      )}
      {noMail && (
        <div className={row}>
          <p className="max-w-[68ch] text-sm" style={{ color: 'var(--color-text-2)' }}>This server reads your mail, but the connection has no Gmail permissions yet: reconnect Google and tick them.</p>
          {action('Reconnect Google')}
        </div>
      )}
      {noDocs && (
        <div className={row}>
          <p className="max-w-[68ch] text-sm" style={{ color: 'var(--color-text-2)' }}>The Meetings page reads the notes Gemini writes, but the connection has no Google Docs permission yet: reconnect Google and tick it.</p>
          {action('Reconnect Google')}
        </div>
      )}
      {noDrive && (
        <div className={row}>
          <p className="max-w-[68ch] text-sm" style={{ color: 'var(--color-text-2)' }}>Sessions can search and read your Google Drive, and write Docs, Sheets and Slides, once the connection has those permissions: reconnect Google and tick them.</p>
          {action('Reconnect Google')}
        </div>
      )}
      {connect.isError && <p role="alert" className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>{(connect.error as Error).message}</p>}
    </>
  );
}

/**
 * Disconnecting Google, at the foot of the section and away from the state lines: a tap on a phone must not stop
 * the calendar by mistake, so it asks first, inline.
 */
function GoogleDisconnect({ serverId }: { serverId: string }) {
  const { data: google } = useGoogle(serverId);
  const disconnect = useDisconnectGoogle(serverId);
  const [confirming, setConfirming] = useState(false);
  const connection = google?.connection;
  if (!connection || connection.status !== 'ok') return null;
  return (
    <div className="mt-5 text-xs" style={{ color: 'var(--color-text-3)' }}>
      {!confirming ? (
        <p className="flex flex-wrap items-baseline gap-x-2">
          Google connected as {connection.email}.
          <button type="button" onClick={() => setConfirming(true)}
            className="py-0.5 hover:underline focus-visible:outline focus-visible:outline-2 max-sm:min-h-10" style={{ color: 'var(--color-text-2)' }}>
            Disconnect Google
          </button>
        </p>
      ) : (
        <div role="group" aria-label="Confirm disconnecting Google" className="rounded-md px-3 py-2.5" style={{ border: '1px solid var(--color-border-2)', backgroundColor: 'var(--color-surface-2)' }}>
          <p className="text-sm" style={{ color: 'var(--color-text)' }}>
            Disconnect {connection.email}? Digests stop waiting for your meetings, and sessions can no longer read or change this calendar.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" variant="danger" className="max-sm:min-h-10" disabled={disconnect.isPending}
              onClick={() => disconnect.mutate(undefined, { onSuccess: () => setConfirming(false) })}>
              {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
            </Button>
            <Button size="sm" variant="ghost" className="max-sm:min-h-10" onClick={() => setConfirming(false)}>Keep connected</Button>
          </div>
          {disconnect.isError && <p role="alert" className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>Not disconnected: {(disconnect.error as Error).message}</p>}
        </div>
      )}
    </div>
  );
}

export function NotificationDelivery({ serverId, isAdmin }: { serverId: string; isAdmin: boolean }) {
  const { data, error, isLoading } = useNotificationStats(serverId);
  // Asked at once, beside the stats, so the calendar line does not arrive a round trip later
  useGoogle(serverId);
  const restore = useRestoreKind(serverId);
  const [editing, setEditing] = useState(false);
  const action = isAdmin && data && !editing ? <SectionAction onClick={() => setEditing(true)}>Change schedule</SectionAction> : undefined;

  if (!data) {
    return (
      <SettingsSection id="notify-title" title="Notifications" loading={isLoading}
        state={error ? `Notification settings not loaded: ${(error as Error).message}` : undefined} tone="error" />
    );
  }
  const quiet = !data.kinds.length && !data.today && !data.queued;
  const t = data.settings.digestTimes;
  const times = t.length > 1 ? `${t.slice(0, -1).join(', ')} and ${t[t.length - 1]}` : (t[0] ?? '');
  const schedule = `${times}${data.settings.weekdaysOnly ? ' on weekdays' : ' every day'}`;

  return (
    <SettingsSection id="notify-title" title="Notifications" action={action}
      state={quiet ? `Nothing sent from this server yet. Urgent decisions go out at once, the others in a digest at ${schedule}.` : summary(data)}>
      <CalendarLine serverId={serverId} isAdmin={isAdmin} stats={data} />
      {editing && <ScheduleForm serverId={serverId} settings={data.settings} onDone={() => setEditing(false)} />}
      <NotifyEverything serverId={serverId} isAdmin={isAdmin} liveUntil={data.liveUntil ?? null} />
      <SettingsPanel>
        <p className="sr-only">Digests at {schedule}, Paris time.</p>
        <DayRuler settings={data.settings} blocked={data.calendar?.status === 'ok' ? data.calendar.now : undefined} liveUntil={data.liveUntil} />
        {data.kinds.length > 0 && (
          <ul className="mt-3" aria-label="Opened, per type">
            {data.kinds.map((k, i) => {
              const warning = downgradeWarning(k);
              return (
                <li key={k.kind} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 py-2 pl-3"
                  style={{ borderLeft: `3px solid ${k.downgraded ? 'var(--color-warn)' : 'transparent'}`, borderTop: i ? '1px solid var(--color-border)' : undefined }}>
                  <span className="min-w-[11rem] flex-1 text-sm" style={{ color: 'var(--color-text)' }}>{KIND[k.kind] ?? k.kind}</span>
                  <span className="text-xs tabular-nums" style={{ color: 'var(--color-text-2)' }}>{k.opened} of {k.sent} opened</span>
                  {warning && <span className="basis-full text-xs" style={{ color: 'var(--color-warn)' }}>{warning}</span>}
                  {k.downgraded && (
                    <span className="flex basis-full items-baseline justify-between gap-3 text-xs" style={{ color: 'var(--color-text-2)' }}>
                      <span>Opened less than 2 times out of the last 10, so now notified one level lower: urgent ones wait for the digest, the others stay on this page.</span>
                      {isAdmin && (
                        <button type="button" disabled={restore.isPending} onClick={() => restore.mutate(k.kind)}
                          className="flex-shrink-0 hover:underline focus-visible:outline focus-visible:outline-2 max-sm:min-h-10" style={{ color: 'var(--color-brand-light)' }}>
                          Restore
                        </button>
                      )}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </SettingsPanel>
      {isAdmin && <GoogleDisconnect serverId={serverId} />}
    </SettingsSection>
  );
}
